import { Form } from '@sravni/cosago-react-library/lib/components';
import { useBoolean } from '@sravni/react-utils';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { checkIsShowAccordionBlockEditIcon } from 'shared/lib';
import { useAppSelector } from 'shared/lib/redux';
import type { IFormHeader } from 'shared/types/IFormHeader';
import { AnketaFormPopup } from 'shared/ui/AnketaFormPopup';
import { CustomAccordionItem } from 'shared/ui/CustomAccordion';

import { selectPassportInfo } from 'src/client/entities/creditForm';

import { useAccordionHeaderParams } from '../../lib/useAccordionHeaderParams';
import { PassportInfoModalTexts } from '../../PassportInfo.texts';
import { Form as FormElement } from '../Form/Form';

export type TPassportInfoAccordionProps = TAccordionProps & {
  shouldRestoreAdditionalData?: boolean;
};

export const AccordionModalForm: FC<TPassportInfoAccordionProps> = ({
  isOpen,
  shouldForceOpenPopup = false,
  isFormForceOpened = false,
  onFormSubmit,
  isLoading,
  shouldRestoreAdditionalData,
  isDisableEdit = false,
  shouldScrollIntoAccordion,
  isPopupClosable = false,
  onPopupClose,
  children,
  isSummary,
  esiaLoginBlock,
  stepIndex,
  isHideAccordionData,
  formId,
  icon,
  submitButtonText,
  isHideDivider = false,
  accordionIconSize,
  TitleComponent,
  DescriptionComponent,
  defaultDescription,
  defaultTitle,
  isAlwaysShowDescription = false,
  onPopupOpen,
  isAnyFormStepOpenedInPopup,
  analyticsOverrides,
}) => {
  const { isFullFilled, data, isOpenedInPopup } = useAppSelector(selectPassportInfo);
  const [shouldShowEditPopup, setShouldShowEditPopup] = useBoolean();
  const [_, setHeader] = useState<IFormHeader>();
  const [title, description] = useAccordionHeaderParams(
    data,
    {
      title: defaultTitle,
      description: defaultDescription,
    },
    isOpen,
    isHideAccordionData,
  );

  const isWithEditItem = checkIsShowAccordionBlockEditIcon(
    isOpen,
    isSummary || isFullFilled,
    isDisableEdit,
    shouldForceOpenPopup,
  );

  const isAccordionOpen = !isFullFilled && isOpen;
  const isEditPopupVisible = isOpenedInPopup || shouldShowEditPopup || (shouldForceOpenPopup && isFormForceOpened);
  // Если shouldForceOpenPopup false, то нужно давать возможнсть отредактировать данные и не дизейблить их
  // сделано для логики короткого продления, когда при восстановлении нет валидации
  const isDisabled = !isOpen && !isFullFilled && (shouldForceOpenPopup || isDisableEdit);
  const descriptionText = useMemo(
    () => (!isDisabled || isAlwaysShowDescription ? description : ''),
    [description, isAlwaysShowDescription, isDisabled],
  );
  const isWithDivider = !isSummary && !isHideDivider;

  const onEditButtonClick = useCallback(() => {
    onPopupOpen?.();
    setShouldShowEditPopup.on();
  }, [onPopupOpen, setShouldShowEditPopup]);

  const onSubmitPopup: IFormPopup['onFormSubmit'] = useCallback(
    async (formData, isDialog) => {
      await onFormSubmit?.(formData, isDialog);
      setShouldShowEditPopup.off();
      onPopupClose?.(true);
    },
    [onFormSubmit, onPopupClose, setShouldShowEditPopup],
  );

  return (
    <>
      <CustomAccordionItem
        onEditButtonClick={onEditButtonClick}
        withDivider={isWithDivider}
        icon={icon}
        iconSize={accordionIconSize}
        isOpen={isAccordionOpen}
        disabled={isDisabled}
        withEdit={isWithEditItem}
        title={TitleComponent ? <TitleComponent disabled={isDisabled}>{title}</TitleComponent> : title}
        description={
          DescriptionComponent ? (
            <DescriptionComponent disabled={isDisabled}>{descriptionText}</DescriptionComponent>
          ) : (
            descriptionText
          )
        }
        scrollIntoView={isAccordionOpen && shouldScrollIntoAccordion}
      >
        {esiaLoginBlock && <Form.Row colWidths={[12]}>{esiaLoginBlock}</Form.Row>}
        <FormElement
          onFormSubmit={onFormSubmit}
          setHeader={setHeader}
          isFormForceOpened={isFormForceOpened}
          isLoading={isLoading}
          shouldRestoreAdditionalData={shouldRestoreAdditionalData}
          stepIndex={stepIndex}
          formId={formId}
          submitButtonText={submitButtonText}
          isAnyFormStepOpenedInPopup={isAnyFormStepOpenedInPopup}
          analyticsOverrides={analyticsOverrides}
        />
      </CustomAccordionItem>

      <AnketaFormPopup
        submitButtonText={submitButtonText}
        isClosable={isPopupClosable}
        isFormForceOpened
        FormElement={FormElement}
        onFormSubmit={onSubmitPopup}
        onClose={(isSubmit) => {
          if (isSubmit) {
            return;
          }
          setShouldShowEditPopup.off();
          onPopupClose?.(false);
        }}
        isLoading={isLoading}
        isOpen={isEditPopupVisible}
        icon={icon}
        iconSize={accordionIconSize}
        title={TitleComponent ? <TitleComponent>{defaultTitle}</TitleComponent> : defaultTitle}
        subtitle={
          DescriptionComponent ? (
            <DescriptionComponent>{PassportInfoModalTexts.description}</DescriptionComponent>
          ) : (
            PassportInfoModalTexts.description
          )
        }
        setHeader={setHeader}
        stepIndex={stepIndex}
        analyticsOverrides={analyticsOverrides}
      >
        {children}
      </AnketaFormPopup>
    </>
  );
};
